import { Avatar } from '@dicebear/core';
import { useEffect, useMemo, useState } from 'react';

import { personasStyle } from '../persona/persona-avatar';

/**
 * A panel in miniature, on the screens before there is one of your own
 * (sign-in, onboarding, an empty home): five faces in the real panel's
 * portrait style, taking turns to react to "your product" — a beat of typing,
 * then a line.
 *
 * Deliberately one line tall, unboxed and quiet: it is there to say "people will look
 * at this", not to compete with the link field below it. Decorative, so hidden
 * from assistive tech; under reduced motion it holds on the first line.
 */
const PANEL = [
  {
    fill: 'bg-persona-3',
    line: 'Where’s the pricing?',
    name: 'Maya',
    role: 'ops lead',
    seed: 'maya-iqbal',
  },
  {
    fill: 'bg-persona-2',
    line: 'Signup took ten seconds. Nice.',
    name: 'Priya',
    role: 'founder',
    seed: 'priya-nair',
  },
  {
    fill: 'bg-persona-1',
    line: 'Not sure what this button does.',
    name: 'Tom',
    role: 'designer',
    seed: 'tom-becker',
  },
  {
    fill: 'bg-persona-0',
    line: 'I’d want to see it before I pay.',
    name: 'Ana',
    role: 'student',
    seed: 'ana-souza',
  },
  {
    fill: 'bg-persona-5',
    line: 'The empty state lost me.',
    name: 'Leo',
    role: 'PM',
    seed: 'leo-park',
  },
] as const;

const TYPING_MS = 900;
const SPEAKING_MS = 3200;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );
}

export function PersonaChorus() {
  const faces = useMemo(
    () =>
      PANEL.map((p) => {
        try {
          return new Avatar(personasStyle, { seed: p.seed }).toDataUri();
        } catch {
          return null;
        }
      }),
    [],
  );
  const [speaker, setSpeaker] = useState(0);
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    const timer = setTimeout(
      () => {
        if (typing) {
          setTyping(false);
        } else {
          setSpeaker((s) => (s + 1) % PANEL.length);
          setTyping(true);
        }
      },
      typing ? TYPING_MS : SPEAKING_MS,
    );
    return () => {
      clearTimeout(timer);
    };
  }, [typing, speaker]);

  const current = PANEL[speaker] ?? PANEL[0];

  return (
    <div
      aria-hidden="true"
      className="flex h-8 max-w-full items-center justify-center gap-3 duration-700 select-none animate-in fade-in motion-reduce:animate-none"
    >
      <span className="flex shrink-0 -space-x-1.5">
        {PANEL.map((p, i) => (
          <span
            className={`relative size-7 overflow-hidden rounded-pill ring-2 transition-transform duration-300 ${p.fill} ${
              i === speaker ? 'z-10 -translate-y-0.5 ring-brand' : 'ring-warm'
            }`}
            key={p.seed}
          >
            {faces[i] ? (
              <img alt="" className="size-full" src={faces[i]} />
            ) : null}
          </span>
        ))}
      </span>

      <span className="flex min-w-0 items-center gap-1.5 text-ui">
        {typing ? (
          <span className="flex items-center gap-1 py-1.5" key="typing">
            {[0, 150, 300].map((delay) => (
              <span
                className="size-1.5 animate-pulse rounded-pill bg-text-muted"
                key={delay}
                style={{ animationDelay: `${String(delay)}ms` }}
              />
            ))}
          </span>
        ) : (
          <span
            className="truncate duration-300 animate-in fade-in slide-in-from-bottom-1"
            key={current.seed}
          >
            <span className="font-semibold text-text-primary">
              {current.name}
            </span>
            <span className="text-text-muted">, {current.role}: </span>
            <span className="text-text-secondary">“{current.line}”</span>
          </span>
        )}
      </span>
    </div>
  );
}
